import {
  Button,
  ChipProps,
  LoadingIndicator,
} from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import { BriefcaseIcon, Download, HomeIcon, Mails } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { DetailSectionComponent } from "../../../components/DetailSectionComponent";
import { UnexpectedErrorDialog } from "../../../components/dialogs/UnexpectedErrorDialog";
import { ProjectDetailsTabs } from "../../projects/pages/ProjectDetailsPage";
import { useProjectDetailsStore, useSignInStore } from "../../store/Hooks";
import {
  RouteSearchParam,
  useRouteNavigator,
} from "../../../routes/RoutesHelper";
import { getValidatedLeoUUIDFromSearchParam } from "../../utils/getValidatedLeoUUIDFromSearchParam";
import {
  useDownloadEmailCycleRecipientsStore,
  useEmailCycleDetailsStore,
  useUpdateEmailCycleStore,
} from "../stores/Hooks";
import { GetEmailCycleDetailsRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getEmailCycleDetailsRPC";
import { EmailCycleDetailsActionButtons } from "../components/buttons/EmailCycleDetailsActionButtons";
import { navigateOrGetNetworkErrorPage } from "../../../utility/navigateOrGetNetworkErrorPage";
import { useNetworkingErrorStore } from "../../networking/store/hooks";
import {
  EmailCycleDetailsPageContent,
  EmailCycleDetailsTabs,
} from "../components/EmailCycleDetailsPageContent";
import { UserPrivileges } from "../../store/user/UserPrivileges";
import { EmailCycleStatus } from "@pulse/pulse-rpcs/lib/email-cycle/emailCycleStatus";
import { GetPresignedDownloadURLForEmailCycleRecipientsRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getPresignedDownloadUrlForEmailCycleRecipientsRPC";
import { getEmailCycleStatusChipProps } from "../utils/getEmailCycleStatusChipProps";
import { getFormattedDateAndTimeString } from "../utils/getFormattedDateAndTimeString";

interface EmailCycleDetailsTab {
  label: string;
  type: EmailCycleDetailsTabs;
}

export const EmailCycleDetailsPage: React.FC = observer(() => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openUnexpectedErrorDialog, setOpenUnexpectedErrorDialog] =
    useState(false);

  const { t } = useTranslation();
  const navigateTo = useRouteNavigator();

  const projectDetailsStore = useProjectDetailsStore();
  const errorStore = useNetworkingErrorStore();
  const emailCycleDetailsStore = useEmailCycleDetailsStore();
  const downloadEmailCycleRecipientsStore =
    useDownloadEmailCycleRecipientsStore();
  const signInStore = useSignInStore();
  const updateEmailCycleStore = useUpdateEmailCycleStore();

  const projectId = searchParams.get("projectId");
  const selectedURLTab = searchParams.get(RouteSearchParam.Tab);
  const tabs: EmailCycleDetailsTab[] = [
    {
      label: t("emailCycles.cycleDetails"),
      type: EmailCycleDetailsTabs.CycleDetails,
    },
    {
      label: t("surveys.respondents"),
      type: EmailCycleDetailsTabs.Respondents,
    },
  ];
  const getDefaultTab = (): EmailCycleDetailsTabs => {
    if (!selectedURLTab) {
      return EmailCycleDetailsTabs.CycleDetails;
    }

    const selectedTab = tabs.find((tab) => tab.type === selectedURLTab)?.type;
    if (!selectedTab) {
      navigateTo.pageNotFound();
      return EmailCycleDetailsTabs.CycleDetails;
    }

    return selectedTab;
  };

  const [selectedTab, setSelectedTab] =
    useState<EmailCycleDetailsTabs>(getDefaultTab);

  const fetchEmailCycleDetails = (): void => {
    const scheduleId = getValidatedLeoUUIDFromSearchParam(
      searchParams,
      RouteSearchParam.ScheduleId,
    )?.uuid;

    if (!scheduleId) {
      navigateTo.pageNotFound();
      return;
    }

    emailCycleDetailsStore
      .getEmailCycleDetails(scheduleId, true)
      .catch(() => setOpenUnexpectedErrorDialog(true));

    if (projectId) {
      projectDetailsStore.getProjectDetails(projectId);
    }
  };

  useEffect(fetchEmailCycleDetails, []);

  useEffect(() => {
    if (!emailCycleDetailsStore.getEmailCycleDetailsRPCError) return;

    switch (emailCycleDetailsStore.getEmailCycleDetailsRPCError) {
      case GetEmailCycleDetailsRPC.RPCError.InvalidScheduleId:
        navigateTo.pageNotFound();
        break;
      default:
        emailCycleDetailsStore.getEmailCycleDetailsRPCError satisfies never;
    }
  }, [emailCycleDetailsStore.getEmailCycleDetailsRPCError]);

  useEffect(() => emailCycleDetailsStore.resetStore, []);

  useEffect(() => {
    switch (
      downloadEmailCycleRecipientsStore.getPresignedDownloadURLForEmailCycleRecipientsRPCError
    ) {
      case undefined:
      case null:
        // Nothing to handle when there is no RPC error.
        break;
      case GetPresignedDownloadURLForEmailCycleRecipientsRPC.RPCError
        .InvalidScheduleId:
        navigateTo.pageNotFound();
        break;
      default:
        downloadEmailCycleRecipientsStore.getPresignedDownloadURLForEmailCycleRecipientsRPCError satisfies never;
    }
  }, [
    downloadEmailCycleRecipientsStore.getPresignedDownloadURLForEmailCycleRecipientsRPCError,
  ]);

  const breadcrumbProps = useMemo(() => {
    const links = [
      {
        label: t("projects.projects"),
        icon: <HomeIcon />,
        onLabelClick: navigateTo.project,
      },
      {
        label: projectDetailsStore.projectName,
        icon: <BriefcaseIcon />,
        onLabelClick: (): void => {
          if (projectId) {
            navigateTo.projectDetails(
              projectId,
              ProjectDetailsTabs.EmailCycles,
            );
          }
        },
      },
    ];

    return {
      links,
      currentLabel: emailCycleDetailsStore.displayCycleName.name,
      currentLabelIcon: <Mails />,
    };
  }, [
    emailCycleDetailsStore.displayCycleName.name,
    navigateTo,
    projectDetailsStore.projectName,
    projectId,
    t,
  ]);

  const handleUnexpectedErrorDialogDone = (): void => {
    setOpenUnexpectedErrorDialog(false);

    if (projectId) {
      navigateTo.projectDetails(projectId, ProjectDetailsTabs.EmailCycles);
      return;
    }

    navigateTo.project();
  };

  const errorPage = navigateOrGetNetworkErrorPage(
    errorStore.error,
    navigateTo,
    errorStore.removeError,
  );

  if (errorPage) {
    errorStore.removeError();
    return errorPage;
  }

  const hasPrivilege = signInStore.isPrivilegeGranted(
    UserPrivileges.processEmailCycle,
  );

  const isQueued =
    emailCycleDetailsStore.status === EmailCycleStatus.EmailCycleStatus.QUEUED;

  const isInProgress =
    emailCycleDetailsStore.status ===
    EmailCycleStatus.EmailCycleStatus.IN_PROGRESS;

  const isRecurring =
    updateEmailCycleStore.scheduleType.type === "RECURRING_SCHEDULE";
  const isImmediate =
    updateEmailCycleStore.scheduleType.type === "IMMEDIATE_SCHEDULE";

  const readOnly =
    !hasPrivilege ||
    isImmediate ||
    !(isRecurring ? isQueued || isInProgress : isQueued);
  const showActionButtons =
    hasPrivilege && (isImmediate ? isQueued : !readOnly);

  const loadingData =
    projectDetailsStore.areProjectDetailsEmpty ||
    emailCycleDetailsStore.loadingEmailCycleDetails;

  const mainHeaderChipProps: ChipProps[] = loadingData
    ? []
    : [
        getEmailCycleStatusChipProps(t, emailCycleDetailsStore.status),
        {
          label: getFormattedDateAndTimeString(
            new Date(emailCycleDetailsStore.scheduledAt),
          ),
          color: "neutral",
        },
      ];

  const getSectionHeaderProps = (): {
    title: string;
    actionElement?: React.ReactElement;
  } => {
    switch (selectedTab) {
      case EmailCycleDetailsTabs.CycleDetails:
        return {
          title: t("emailCycles.cycleDetails"),
          actionElement:
            showActionButtons &&
            !emailCycleDetailsStore.loadingEmailCycleDetails ? (
              <EmailCycleDetailsActionButtons
                fetchEmailCycleDetails={fetchEmailCycleDetails}
              />
            ) : undefined,
        };
      case EmailCycleDetailsTabs.Respondents:
        return {
          title: t("surveys.respondents"),
          actionElement: (
            <Button
              name="downloadCSV"
              size="medium"
              variant="outlined-neutral"
              label={t("common.downloadCSV")}
              icon={<Download />}
              iconPosition="leading"
              loading={
                downloadEmailCycleRecipientsStore.isDownloadingRecipients
              }
              onClick={() => {
                void downloadEmailCycleRecipientsStore.downloadEmailCycleRecipients(
                  emailCycleDetailsStore.scheduleId,
                  emailCycleDetailsStore.cycleName.name,
                );
              }}
            />
          ),
        };
      default:
        return selectedTab satisfies never;
    }
  };

  return (
    <>
      <DetailSectionComponent
        isManageUsersLogoVisible={true}
        isAuditLogsLogoVisible={true}
        mainHeaderProps={{
          title: emailCycleDetailsStore.displayCycleName.name,
          icon: <Mails />,
          prefersLargeTitle: true,
          chipProps: mainHeaderChipProps,
        }}
        breadcrumbProps={breadcrumbProps}
        tabsProps={{
          tabs,
          selectedTabIndex: tabs.findIndex((tab) => tab.type === selectedTab),
          onTabChange: (selectedIndex: number): void => {
            const selectedTabType =
              tabs[selectedIndex] ?? EmailCycleDetailsTabs.CycleDetails;

            setSearchParams((prev) => {
              prev.set(RouteSearchParam.Tab, selectedTabType.type);
              return prev;
            });
            setSelectedTab(selectedTabType.type);
          },
        }}
        sectionHeaderProps={{
          ...getSectionHeaderProps(),
          prefersLargeTitle: true,
        }}
        isChildHeightVariable={false}
        isOverflowHidden={false}
      >
        {loadingData ? (
          <LoadingIndicator variant="overlay" isLoading />
        ) : (
          <EmailCycleDetailsPageContent selectedTab={selectedTab} />
        )}
      </DetailSectionComponent>

      {openUnexpectedErrorDialog && (
        <UnexpectedErrorDialog
          message={t("emailCycles.getEmailCycleDetailsUnexpectedErrorMessage")}
          onClickDone={handleUnexpectedErrorDialogDone}
        />
      )}

      {downloadEmailCycleRecipientsStore.hasUnexpectedDownloadError && (
        <UnexpectedErrorDialog
          message={t(
            "emailCycles.getEmailCycleRecipientsUnexpectedErrorMessage",
          )}
          onClickDone={downloadEmailCycleRecipientsStore.resetStore}
        />
      )}
    </>
  );
});
